import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import { Button } from "../ui/button";
import ConsignmentConfigModal from "./ConsignmentConfigModal";
import { useAuth } from "../../contexts/AuthContext";
import { useScopedBoxes } from "../../hooks/useScopedBoxes";
import { fetchCompanies, type Company } from "../../services/adminApi";
import {
  type ConsignmentInvoicePreview,
  type ConsignmentPreview,
  downloadBlob,
  fetchConsignmentCsv,
  fetchConsignmentFile,
  fetchConsignmentPdf,
  fetchConsignmentPreview,
  fetchConsignmentZip,
} from "../../services/consignmentApi";

/**
 * Weekly Consignment report card.
 *
 * Scope locks:
 *   - customer: customer pinned to their company; vendor + smartbox optional
 *   - vendor:   must pick a customer; vendor pinned from their company
 *   - platform: must pick a customer; vendor + smartbox optional
 *
 * Default week = the most-recent COMPLETED week per the customer's
 * `consignment_cutoff_dow` / `_hour` setting. The "Previous week"
 * arrow walks further back; the picker accepts any date and snaps it
 * to the preceding Sunday so the report always covers Sun-Sat
 * (Jason 2026-07-07).
 */

/** Roll any YYYY-MM-DD date back to the preceding Sunday (Sunday itself is
 *  unchanged). Returns "" for empty input. Uses UTC arithmetic so DST
 *  transitions in the local zone can't shift the returned day. */
function snapToSunday(dateStr: string): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(Date.UTC(y, m - 1, d));
  const daysSinceSunday = dt.getUTCDay(); // 0=Sun ... 6=Sat
  dt.setUTCDate(dt.getUTCDate() - daysSinceSunday);
  return dt.toISOString().slice(0, 10);
}

/** Format the Sun-Sat window for the picker helper text. Both bounds
 *  inclusive; the backend's period is [start, start+7). */
function sundayThroughSaturday(dateStr: string): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, d));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (dt: Date): string =>
    `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  return `${fmt(start)} → ${fmt(end)}`;
}

export const ConsignmentReportSection: React.FC = () => {
  const { token, role, user } = useAuth();
  const { snapshot } = useScopedBoxes();
  // Vendors don't get the config modal -- server also 403s them.
  const canManageConfig = role === "customer" || role === "platform";
  const [showConfig, setShowConfig] = useState(false);

  // ---------- Scope state ----------
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customerId, setCustomerId] = useState<number | "">(
    role === "customer" && user?.company_id ? user.company_id : "",
  );
  const [vendorId, setVendorId] = useState<number | "">("");
  const [smartboxId, setSmartboxId] = useState<number | "">("");
  const [weeksBack, setWeeksBack] = useState<number>(1);
  const [weekOverride, setWeekOverride] = useState<string>("");

  // Smartbox picker options -- pulled from the scoped boxes snapshot the
  // user can see (server enforces the actual permission). Filtered to
  // the selected customer so a platform user picking Nabors doesn't
  // see every other customer's boxes.
  const smartboxOptions = useMemo(() => {
    const rows = (snapshot?.boxes ?? [])
      .filter((b) => b.smartbox_id != null)
      .filter((b) => {
        // Customer filter: match Box.customer to the selected company's name.
        if (customerId === "") return true;
        const c = companies.find((c) => c.company_id === Number(customerId));
        return c ? b.customer === c.company_name : true;
      })
      .map((b) => ({
        id: b.smartbox_id as number,
        code: b.smartbox_code ?? "?",
        workcenter: b.work_center ?? "",
      }));
    // Dedup by id (a customer might have the same smartbox appear once
    // per SCU; report keys by smartbox_id).
    const seen = new Set<number>();
    const out = [];
    for (const r of rows) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    out.sort((a, b) =>
      (a.workcenter || "").localeCompare(b.workcenter || "") ||
      a.code.localeCompare(b.code),
    );
    return out;
  }, [snapshot, customerId, companies]);

  // ---------- Result state ----------
  const [preview, setPreview] = useState<ConsignmentPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load companies (platform/vendor need the customer picker; customer is auto-pinned).
  useEffect(() => {
    if (!token || role === "customer") return;
    fetchCompanies(token)
      .then((cs) => setCompanies(cs))
      .catch(() => setCompanies([]));
  }, [token, role]);

  const customers = useMemo(
    () => companies.filter((c) => c.company_type === "customer"),
    [companies],
  );
  const vendors = useMemo(
    () => companies.filter((c) => c.company_type === "vendor"),
    [companies],
  );

  const queryOpts = useMemo(() => {
    const opts: Record<string, number | string | undefined> = {};
    if (customerId !== "") opts.customer = Number(customerId);
    if (vendorId !== "") opts.vendor = Number(vendorId);
    if (smartboxId !== "") opts.smartbox = Number(smartboxId);
    if (weekOverride) opts.week = weekOverride;
    else opts.weeksBack = weeksBack;
    return opts as {
      customer?: number; vendor?: number; smartbox?: number;
      week?: string; weeksBack?: number;
    };
  }, [customerId, vendorId, smartboxId, weekOverride, weeksBack]);

  const canLoad = role === "customer" || customerId !== "";

  const onLoad = async (): Promise<void> => {
    if (!token || !canLoad) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const p = await fetchConsignmentPreview(token, queryOpts);
      setPreview(p);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load report";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onDownloadAllZip = async (): Promise<void> => {
    if (!token || !canLoad) return;
    try {
      const { blob, filename } = await fetchConsignmentZip(token, queryOpts);
      downloadBlob(blob, filename ?? "consignment.zip");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const onDownloadAllCsv = async (): Promise<void> => {
    if (!token || !canLoad) return;
    try {
      const { blob, filename } = await fetchConsignmentCsv(token, queryOpts);
      downloadBlob(blob, filename ?? "consignment.csv");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const onDownloadAllPdf = async (): Promise<void> => {
    if (!token || !canLoad) return;
    try {
      const { blob, filename } = await fetchConsignmentPdf(token, queryOpts);
      downloadBlob(blob, filename ?? "consignment.pdf");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const onDownloadOne = async (
    inv: ConsignmentInvoicePreview, fmt: "json" | "csv" | "pdf",
  ): Promise<void> => {
    if (!token) return;
    try {
      const { blob, filename } = await fetchConsignmentFile(
        token, inv.file_name_stem, fmt, queryOpts,
      );
      downloadBlob(blob, filename ?? `${inv.file_name_stem}.${fmt}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download failed");
    }
  };

  // Group invoices by smartbox for display (one C+R block per smartbox).
  const groupedBySmartbox = useMemo(() => {
    const groups: Record<string, ConsignmentInvoicePreview[]> = {};
    for (const inv of preview?.invoices ?? []) {
      const key = `${inv.smartbox_id}:${inv.smartbox}`;
      (groups[key] ||= []).push(inv);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [preview]);

  // Pretty-print the period.
  const periodLabel = useMemo(() => {
    if (!preview) return "";
    const s = preview.meta.period_start_utc.slice(0, 10);
    const e = preview.meta.period_end_utc.slice(0, 10);
    return `${s} → ${e}`;
  }, [preview]);

  return (
    <div className="space-y-4">
      <ConsignmentConfigModal
        open={showConfig}
        onClose={() => setShowConfig(false)}
        customerId={
          // Customer callers: server pins to their own cid, no need to
          // pass one. Platform callers: pass the selected customer so
          // the export is scoped; if none picked, server returns all.
          role === "customer" || customerId === ""
            ? undefined
            : Number(customerId)
        }
      />
      {/* Filters */}
      <div className="bg-white border border-secondary-200 rounded-md p-3 space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          {role !== "customer" && (
            <label className="text-xs text-secondary-600">
              <div className="mb-0.5">Customer</div>
              <select
                className="px-2 py-1.5 border border-secondary-300 rounded text-sm bg-white min-w-[180px]"
                value={customerId}
                onChange={(e) =>
                  setCustomerId(e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">Select…</option>
                {customers.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {role !== "vendor" && (
            <label className="text-xs text-secondary-600">
              <div className="mb-0.5">Vendor (optional)</div>
              <select
                className="px-2 py-1.5 border border-secondary-300 rounded text-sm bg-white min-w-[180px]"
                value={vendorId}
                onChange={(e) =>
                  setVendorId(e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">All vendors</option>
                {vendors.map((v) => (
                  <option key={v.company_id} value={v.company_id}>
                    {v.company_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="text-xs text-secondary-600">
            <div className="mb-0.5">Smartbox (optional)</div>
            <select
              className="px-2 py-1.5 border border-secondary-300 rounded text-sm bg-white min-w-[220px]"
              value={smartboxId}
              onChange={(e) =>
                setSmartboxId(e.target.value ? Number(e.target.value) : "")
              }
              disabled={smartboxOptions.length === 0}
            >
              <option value="">All smartboxes</option>
              {smartboxOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.workcenter ? `${s.workcenter} (${s.code})` : s.code}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-secondary-600">
            <div className="mb-0.5">Week</div>
            <select
              className="px-2 py-1.5 border border-secondary-300 rounded text-sm bg-white"
              value={weekOverride ? "custom" : String(weeksBack)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") return;
                setWeekOverride("");
                setWeeksBack(Number(v));
              }}
            >
              <option value="1">Last completed</option>
              <option value="2">2 weeks ago</option>
              <option value="3">3 weeks ago</option>
              <option value="4">4 weeks ago</option>
              <option value="0">Current (in-progress)</option>
              {weekOverride && <option value="custom">Custom</option>}
            </select>
          </label>

          <label className="text-xs text-secondary-600">
            <div className="mb-0.5">Or pick any date in the week</div>
            <input
              type="date"
              value={weekOverride}
              onChange={(e) => setWeekOverride(snapToSunday(e.target.value))}
              className="px-2 py-1.5 border border-secondary-300 rounded text-sm bg-white"
            />
            {weekOverride && (
              <div className="mt-0.5 text-[11px] text-secondary-500">
                Sun-Sat: {sundayThroughSaturday(weekOverride)}
              </div>
            )}
          </label>

          <div className="flex gap-1.5 ml-auto">
            {canManageConfig && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowConfig(true)}
              >
                Requesters &amp; Ship-to
              </Button>
            )}
            <Button size="sm" onClick={onLoad} disabled={!canLoad || loading}>
              {loading ? "Loading…" : "Load"}
            </Button>
            {preview && preview.invoices.length > 0 && (
              <>
                <Button
                  size="sm" variant="outline"
                  onClick={onDownloadAllPdf}
                >
                  Download PDF
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={onDownloadAllZip}
                >
                  Download ZIP
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={onDownloadAllCsv}
                >
                  Download CSV
                </Button>
              </>
            )}
          </div>
        </div>
        {!canLoad && (
          <p className="text-[11px] text-secondary-500">
            Select a customer to load this report.
          </p>
        )}
      </div>

      {/* Meta + diagnostics */}
      {error && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {loading && <LoadingSpinner label="building report" />}

      {preview && !loading && (
        <>
          <div className="bg-secondary-50 border border-secondary-200 rounded px-3 py-2 text-xs text-secondary-700 flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <strong>Period:</strong> {periodLabel} ({preview.meta.tz})
            </span>
            <span>
              <strong>Events:</strong> {preview.meta.event_count.toLocaleString()}
            </span>
            <span>
              <strong>Invoices:</strong> {preview.meta.invoice_count}
            </span>
            {Object.values(preview.meta.orphans).some((n) => n > 0) && (
              <span className="text-warning-800">
                <strong>Orphans:</strong>{" "}
                {Object.entries(preview.meta.orphans)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${k.replace(/_/g, " ")}=${n}`)
                  .join(", ")}{" "}
                — these events lack the price / vendor / item config
                needed to invoice; fix in Settings.
              </span>
            )}
          </div>

          {preview.invoices.length === 0 ? (
            <div className="text-sm text-secondary-700 bg-success-50 border border-success-200 p-6 rounded-md">
              ✓ No invoiceable events in this window.
            </div>
          ) : (
            <div className="space-y-4">
              {groupedBySmartbox.map(([key, invoices]) => (
                <div
                  key={key}
                  className="bg-white border border-secondary-200 rounded-md overflow-hidden"
                >
                  <div className="px-3 py-2 bg-secondary-50 border-b border-secondary-200 text-sm font-semibold">
                    {invoices[0].smartbox}
                    <span className="text-secondary-500 font-normal ml-2 text-xs">
                      · {invoices[0].deliver_to_loc}
                    </span>
                  </div>
                  <div className="divide-y divide-secondary-100">
                    {invoices.map((inv) => (
                      <div key={inv.file_name_stem} className="p-3">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                          <span
                            className={
                              "inline-flex items-center text-[11px] px-1.5 py-0.5 rounded font-semibold " +
                              (inv.kind === "CONSIGNMENT"
                                ? "bg-primary-100 text-primary-700"
                                : "bg-warning-100 text-warning-800")
                            }
                          >
                            {inv.kind}
                          </span>
                          <span className="font-mono text-sm">{inv.doc_num}</span>
                          <span className="text-sm text-secondary-700">
                            {inv.vendor}
                          </span>
                          <span className="text-sm">
                            <span className="text-secondary-500">Lines:</span>{" "}
                            {inv.line_count}
                          </span>
                          <span className="text-sm font-semibold">
                            <span className="text-secondary-500 font-normal">
                              Total:
                            </span>{" "}
                            ${inv.total}
                          </span>
                          {inv.requester && (
                            <span className="text-xs text-secondary-500">
                              requester {inv.requester}
                            </span>
                          )}
                          <div className="flex gap-1 ml-auto">
                            <Button
                              size="sm" variant="outline"
                              onClick={() => onDownloadOne(inv, "pdf")}
                            >
                              PDF
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => onDownloadOne(inv, "json")}
                            >
                              JSON
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => onDownloadOne(inv, "csv")}
                            >
                              CSV
                            </Button>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-secondary-50 text-secondary-600 uppercase tracking-wide">
                              <tr>
                                <th className="text-left px-2 py-1">Product</th>
                                <th className="text-left px-2 py-1">Part #</th>
                                <th className="text-left px-2 py-1">Cust part</th>
                                <th className="text-right px-2 py-1">Price</th>
                                <th className="text-right px-2 py-1">Qty</th>
                                <th className="text-right px-2 py-1">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inv.payload.list.map((ln, i) => (
                                <tr key={`${inv.doc_num}:${i}`} className="border-t border-secondary-100">
                                  <td className="px-2 py-1">{ln.productName}</td>
                                  <td className="px-2 py-1 font-mono">{ln.partNo || "—"}</td>
                                  <td className="px-2 py-1 font-mono text-secondary-600">
                                    {ln.custpart || "—"}
                                  </td>
                                  <td className="px-2 py-1 text-right">${ln.price}</td>
                                  <td className="px-2 py-1 text-right">{ln.qty}</td>
                                  <td className="px-2 py-1 text-right font-semibold">
                                    ${ln.total}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConsignmentReportSection;
